import dayjs from "dayjs";
import _ from "lodash";
import { getCareerAvailableYears } from "../../lib/cvDataUtilities.js";
import { formatDate, getDate } from "../../lib/dateHelpers.js";
import { escapeHtml } from "../../lib/frontendUtilities.js";
import { getLastPosition } from "../../lib/reportUtilities.js";
import { getKey } from "../../lib/utilities.js";
import { CCManage } from "../cclib/CCManage.ts";
import * as conference_helper from "../conference_helper.js";
class ConferenceStructure {
  constructor(choices = []) {
    // This is like the 'format input' object part that defines the layout of tab
    // name: label next to the text box
    // type: integer, float, string, header, paragraph, select (option), previous (last year's value)
    this.structure = [
      {
        name: "Colleague Name",
        type: "string",
        key: "colleague_name",
        readOnly: true
      },
      {
        type: "inline-table",
        sections: [
          {
            name: "Colleague Job Title",
            type: "string",
            key: "job_title"
          },
          {
            name: "Start Date",
            type: "date",
            key: "job_start_date"
          }
        ]
      },
      {
        type: "inline-table",
        sections: [
          {
            name: "Secondary Job Title (optional)",
            type: "string",
            key: "secondary_job_title",
            optional: true
          },
          {
            name: "Start Date",
            type: "date",
            key: "secondary_job_start_date",
            optional: true
          }
        ]
      },
      {
        name: "Academic Rank",
        type: "string",
        key: "academic_rank",
        readOnly: true
      },
      {
        name: "Time in Academic Rank (Years)",
        type: "string",
        key: "time_in_position",
        readOnly: true
      },
      {
        name: "Appraiser's Name",
        type: "string",
        key: "appraiser_name",
        readOnly: true
      },
      {
        name: "Appraisal Period",
        type: "string",
        key: "appraisal_period",
        readOnly: true
      },
      { name: "Colleague ID", type: "string", key: "colleague_id" },
      {
        name: "Department Name",
        type: "string",
        key: "department_name",
        readOnly: true
      },
      {
        name: "Department ID",
        type: "string",
        key: "department_id",
        readOnly: true
      },
      { name: "Date started at Lahey", type: "date", key: "lahey_start_date" },
      {
        name: "Date Appraisal Completed",
        type: "string",
        key: "date_appraisal_completed",
        readOnly: true,
        optional: true
      },
      {
        name: "MOC Participant (Y/N)",
        type: "select",
        key: "moc_participant",
        options: ["Yes", "No"]
      },
      {
        name: "Primary Subspecialty Section",
        type: "select",
        key: "subspecialty",
        options: choices
      },
      { name: "Job Satisfaction / Burnout", type: "header" },
      {
        name: "How would you rate your overall job satisfaction for the past 12 months (scale worst 1-10 best)",
        type: "select",
        key: "jobs_satisfaction_current",
        options: ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10"]
      },
      {
        name: "Last year's rating",
        type: "previous",
        key: "jobs_satisfaction_current_prev"
      },
      {
        name: "Comments on job satisfaction",
        type: "paragraph",
        key: "jobs_satisfaction_comments",
        optional: true
      },
      { name: "Do you feel burned out?", type: "header" },
      {
        name: "This year: (Yes/No)",
        type: "select",
        key: "burnout_current",
        options: ["Yes", "No"]
      },
      {
        name: "Last year: (Yes/No)",
        type: "previous",
        key: "burnout_current_prev"
      },
      {
        name: "Comments on burnout",
        type: "paragraph",
        key: "burnout_comments",
        optional: true
      },
      {
        name: "Appraiser Comments",
        type: "paragraph",
        key: "jobs_satisfaction_appraiser_comments",
        isMentorField: true,
        optional: true
      },
      { name: "Job Satisfiers / Dis-satisfiers", type: "header" },
      { name: "Job Satisfiers", type: "paragraph", key: "job_satisfiers" },
      {
        name: "Job Dissatisfiers",
        type: "paragraph",
        key: "job_dissatisfiers"
      },
      {
        name: "Appraiser Comments",
        type: "paragraph",
        key: "job_dissatisfiers_appraiser_comments",
        isMentorField: true,
        optional: true
      },
      { name: "What are your career goals?", type: "header" },
      {
        name: "Immediate goals (~1-2 years)",
        type: "paragraph",
        key: "immediate_goals"
      },
      {
        name: "Long term goals (~5 years)",
        type: "paragraph",
        key: "long_term_goals"
      },
      {
        name: "Appraiser Comments",
        type: "paragraph",
        key: "long_term_goals_appraiser_comments",
        isMentorField: true,
        optional: true
      },
      { name: "Goals & Objectives", type: "header" },
      {
        name: "Clinical Goals",
        type: "table",
        sections: [
          {
            name: "Prior year goal(s)",
            type: "previous",
            key: "clinical_goals_future_prev",
            optional: true,
            paragraph: true
          },
          {
            name: "Progress/Gap Analysis/Opportunities",
            type: "paragraph",
            key: "clinical_goals_progress"
          },
          {
            name: "Future/Next Year Goal(s)",
            type: "paragraph",
            key: "clinical_goals_future"
          },
          {
            name: "Comment(s)",
            type: "paragraph",
            key: "clinical_goals_comment"
          }
        ]
      },
      {
        name: "Appraiser Comments",
        type: "paragraph",
        key: "clinical_goals_appraiser_comments",
        isMentorField: true,
        optional: true
      },
      {
        name: "Education and Research Goals",
        type: "table",
        sections: [
          {
            name: "Prior year goal(s)",
            type: "previous",
            key: "education_goals_future_prev",
            paragraph: true
          },
          {
            name: "Progress/Gap Analysis/Opportunities",
            type: "paragraph",
            key: "education_goals_progress"
          },
          {
            name: "Future/Next Year Goal(s)",
            type: "paragraph",
            key: "education_goals_future"
          },
          {
            name: "Comment(s)",
            type: "paragraph",
            key: "education_goals_comment"
          }
        ]
      },
      {
        name: "Appraiser Comments",
        type: "paragraph",
        key: "edu_goals_appraiser_comments",
        isMentorField: true,
        optional: true
      },
      {
        name: "Academic Advancement Goals",
        type: "table",
        sections: [
          {
            name: "Prior year goal(s)",
            type: "previous",
            key: "academic_goals_future_prev",
            paragraph: true
          },
          {
            name: "Progress/Gap Analysis/Opportunities",
            type: "paragraph",
            key: "academic_goals_progress"
          },
          {
            name: "Future/Next Year Goal(s)",
            type: "paragraph",
            key: "academic_goals_future"
          },
          {
            name: "Comment(s)",
            type: "paragraph",
            key: "academic_goals_comment"
          }
        ]
      },
      {
        name: "Appraiser Comments",
        type: "paragraph",
        key: "adv_goals_appraiser_comments",
        isMentorField: true,
        optional: true
      },
      {
        name: "Other Goals (specify)",
        type: "table",
        sections: [
          {
            name: "Prior year goal(s)",
            type: "previous",
            key: "other_goals_future_prev",
            paragraph: true
          },
          {
            name: "Progress/Gap Analysis/Opportunities",
            type: "paragraph",
            key: "other_goals_progress"
          },
          {
            name: "Future/Next Year Goal(s)",
            type: "paragraph",
            key: "other_goals_future"
          },
          {
            name: "Comment(s)",
            type: "paragraph",
            key: "other_goals_comment"
          }
        ]
      },
      {
        name: "Appraiser Comments",
        type: "paragraph",
        key: "other_goals_appraiser_comments",
        isMentorField: true,
        optional: true
      },
      {
        name: "Please summarize comments for this evaluation period in 1-2 sentences (include honors, accomplishments if any)",
        type: "header"
      },
      {
        name: "Appraiser Comments",
        type: "paragraph",
        key: "appraiser_comments",
        isMentorField: true
      },
      {
        name: "Colleague Comments",
        type: "paragraph",
        key: "colleague_comments"
      }
    ];
  }
  replace(key, value) {
    let i = this.structure.findIndex((element) => element.key == key);
    if (i != -1) {
      this.structure.splice(i, 1, value);
    }
    return i;
  }
  insertAfter(key, value) {
    let i = this.structure.findIndex((element) => element.key == key);
    if (i != -1) {
      this.structure.splice(++i, 0, value);
    }
    return i;
  }
  insertAt(index, value) {
    this.structure.splice(index, 0, value);
    return ++index;
  }
  insertBefore(key, value) {
    let i = this.structure.findIndex((element) => element.key == key);
    if (i != -1) {
      this.structure.splice(i--, 0, value);
    }
    return i;
  }
  getIndex(key) {
    return this.structure.findIndex((element) => element.key == key);
  }
  getIndexByName(name) {
    return this.structure.findIndex((element) => element.name == name);
  }
  remove(key) {
    const i = this.structure.findIndex((element) => element.key == key);
    if (i != -1) this.structure.splice(i, 1);
  }
  removeByName(name) {
    const i = this.structure.findIndex((element) => element.name == name);
    if (i != -1) this.structure.splice(i, 1);
  }
}

let overviewState,
  yearInfo,
  availableYears,
  baseYear,
  viewYear,
  cutoff,
  month,
  date,
  startDate,
  endDate,
  confYearData,
  isOpen;
let structure = new ConferenceStructure();

function overrideStructure(struct) {
  structure = struct;
}

function updateOverviewState(sectionState, section) {
  overviewState = getKey(sectionState, section, {});
}

function updateYearInfo(
  newDef,
  conference,
  user,
  cvOwner,
  sectionState,
  section,
  managerAccess
) {
  yearInfo = getCareerAvailableYears(newDef, conference, user, cvOwner);
  availableYears = yearInfo.availableYears;
  baseYear = yearInfo.baseYear;
  if (overviewState == undefined) updateOverviewState(sectionState, section);
  if (
    overviewState.yearIndex == null ||
    availableYears[overviewState.yearIndex] == null
  ) {
    overviewState.yearIndex = availableYears.findIndex((n) => n == baseYear);
  }
  viewYear = availableYears[overviewState.yearIndex];
  cutoff = conference.cutoffDate || "1/1"; // Before this = last year. New section starts on this date
  month = cutoff.split("/")[0] || "1";
  date = cutoff.split("/")[1] || "1";
  startDate = new Date(month + "/" + date + "/" + viewYear).getTime();
  endDate =
    new Date(month + "/" + date + "/" + (viewYear + 1)).getTime() -
    1000 * 60 * 60 * 24;

  confYearData = conference.openYears[viewYear] || { open: false };
  isOpen = !!confYearData.open || managerAccess;
}

function populateReadonlyValues(pairs, newDef, dataPrefix) {
  pairs.forEach((pair) => {
    $(`input[name='${pair.key}']`).first().val(pair.value);
    newDef.career[viewYear].data[dataPrefix + pair.key] = pair.value;
  });
}
function setAppraisalCompletedDate(mentorAccess, newDef, dataPrefix, yearData) {
  if (mentorAccess) {
    $("input[name='date_appraisal_completed']")
      .first()
      .val(
        yearData?.data?.chairSign
          ? dayjs(yearData?.data?.chairSign.time).format("M/D/YYYY")
          : ""
      );
    newDef.career[viewYear].data[dataPrefix + "date_appraisal_completed"] = $(
      "input[name='date_appraisal_completed']"
    )
      .first()
      .val();
  }
}

function createSignSection(printableContents) {
  printableContents.append(
    $(
      "" +
        `<div id="lahSignSection">
      <div style="margin-bottom: 5px;">
      <h4 id="lahSignChairLine" style="display: inline-block;margin-right: 10px; font-weight: normal;"><b>Signature of Appraiser: </b><i>(Not Yet Signed)</i></h4><div id="lahSignChairButton" class="btn btn-success">Digitally Sign</div>
      </div>
      <div style="margin-bottom: 5px;">
      <h4 id="lahSignMenteeLine" style="display: inline-block;margin-right: 10px; font-weight: normal;"><b>Signature of Colleague: </b></h4><div id="lahSignMenteeButton" class="btn btn-success">Digitally Sign</div>
      </div>
      </div>`
    )
  );
}

function addAcademicTitle(last) {
  const dateText = getDate(last);
  const yearsLong = Math.round(last.years * 10) / 10; // Math.round(((last.time.end != null && last.time.end != 'ongoing') ? getYears(last.time.end) : (new Date().getYear()+1900)) - getYears(last.time.start));
  $("#fieldList").append(
    $(
      "" +
        '<div style="margin-bottom: 10px;margin-top: 10px;">' +
        '  <h4 style="margin-bottom: 10px; display: inline-block;">Academic Title: <span id="careerConfCurrentTitle"><u class="sectionTab" data-section="facultyAppointments">' +
        escapeHtml(last.title) +
        " (" +
        escapeHtml(dateText) +
        ")</u> for " +
        yearsLong +
        " year" +
        (yearsLong != 1 ? "s" : "") +
        "</span></h4>" +
        "<div>"
    )
  );
}

function createStatusButtons() {
  $("#fieldList").append(
    $(
      "" +
        '<div style="margin-top: 8px;display: inline-block;width: 100%;">' +
        '  <h4 class="statusTitle" style="display: inline-block; margin-right: 5px;">Status: Colleague Editing</h4>' +
        '  <div class="btn nextStatus btn-success" style="vertical-align: top;margin-top: 3px;"></div>' +
        '  <div class="btn prevStatus btn-default" style="vertical-align: top;margin-top: 3px;"></div>' +
        '  <h5 class="errorSubheader" style="color: #ad310e;">Missing Information Required Before Submission</h5>' +
        '  <h5 class="statusSubheader">When finished, mark as complete to notify your appraiser.</h5>' +
        "</div>"
    )
  );
}

function listMentors(mentors, mentorList) {
  if (mentorList.length > 0) {
    $("#fieldList").append(
      $(
        "" +
          '<div id="confSelectSection" style="margin-bottom: 10px;display: inline-block;width: 100%;">' +
          '  <h4 style="display: inline-block;margin-right: 5px;">Annual Review Appraiser' +
          (mentorList.length > 1 ? "s" : "") +
          ": " +
          mentorList
            .map(function (mId) {
              return escapeHtml(mentors[mId]?.name);
            })
            .join(", ") +
          "</h4>" +
          "</div>"
      )
    );
  }
}

function createExportButtons(cvOwner, customExportSettings, updateHTML) {
  $("#fieldList").append(
    $(
      "" +
        "<div>" +
        '   <div class="btn btn-default careerYearPrev" style="margin-right: 15px; margin-bottom: 8px;">' +
        '   <span class="glyphicon glyphicon-arrow-left"></span>' +
        "   </div>" +
        '   <h4 class="careerYear" style="margin-right: 15px;display: inline-block;margin-top: 7px;vertical-align: top;">Year: </h4>' +
        '   <div class="btn btn-default careerYearNext" style="margin-right: 15px;vertical-align: top;"><span class="glyphicon glyphicon-arrow-right"></span></div>' +
        '   <div class="btn btn-primary limitedCVCareerDownload" style="position: relative; margin-right: 5px; vertical-align: top; display: inline-block;">' +
        '     <span class="glyphicon glyphicon-download" style="margin-right: 5px;"></span>' +
        "     <span>Date Limited CV</span>" +
        "   </div>" +
        '   <div class="btn btn-primary fullDownload" style="position: relative; margin-right: 5px; vertical-align: top; display: inline-block;">' +
        '     <span class="glyphicon glyphicon-download" style="margin-right: 5px;"></span>' +
        "     <span>Full CV</span>" +
        "   </div>" +
        '   <div class="btn btn-primary reviewForm" style="position: relative; margin-right: 5px; vertical-align: top; display: inline-block;">' +
        '     <span class="glyphicon glyphicon-download" style="margin-right: 5px;"></span>' +
        "     <span>Download Eval</span>" +
        "   </div>" +
        '   <div class="btn btn-primary careerReportPrint" style="position: relative; margin-right: 5px; vertical-align: top; display: inline-block;">' +
        '     <span class="glyphicon glyphicon-print" style="margin-right: 5px;"></span>' +
        "     <span>Print</span>" +
        "   </div>" +
        " </div>"
    )
  );
  // Print button functionality; Do we want this?
  $(".careerReportPrint")
    .unbind("click")
    .click(function () {
      updateHTML({ readOnly: true });
      const printContents = $("#printableContents")[0].innerHTML;
      const w = window.open("", "Test");
      if (w == null) return;
      w.document.write(
        '<link rel="stylesheet" href="/css/bootstrap.min.css"/>'
      );
      w.document.write(
        "<style> @media print {  @page { margin: 1in; } } </style>"
      );
      const yearRange = $(".careerYear").text();
      w.document.write(
        '<div style="padding: 15px;"><h4 style="">' +
          yearRange +
          " " +
          (cvOwner.name || "") +
          " Annual Performance Evaluation</h4>" +
          printContents +
          "</div>"
      );
      updateHTML();
      w.document.title =
        yearRange +
        " " +
        (cvOwner.name || "") +
        " Annual Performance Evaluation";
      setTimeout(function () {
        w.print();
        // w.close();
      }, 1000);
    });
  // Date limited CV download button functionality
  $(".limitedCVCareerDownload")
    .unbind("click")
    .click(function () {
      customExportSettings.dateLimited = true;
      customExportSettings.shown = true;
      $("#m1").val(month);
      $("#m2").val(new Date(endDate).getMonth() + 1);
      $("#d1").val(date);
      $("#d2").val(new Date(endDate).getDate());
      $("#y1").val(viewYear);
      $("#y2").val(new Date(endDate).getYear() + 1900);
      $(".download").trigger("click");
    });
  $(".fullDownload")
    .off("click")
    .on("click", () => {
      customExportSettings.dateLimited = false;
      $(".download").trigger("click");
    });
}

function updateStatusButtons(
  yearData,
  mentorList,
  cvOwner,
  user,
  newDef,
  backend,
  updateHTML,
  checkSave
) {
  $(".nextStatus")
    .unbind("click")
    .click(function () {
      if ($(this).hasClass("disabled")) {
        $(".has-error")[0].scrollIntoView();
        return;
      }
      const logText = JSON.stringify({
        cvOwnerId: cvOwner.id,
        userId: user.id,
        data: newDef.career
      });
      backend.send("log", logText);

      if (yearData.status == "mentee") {
        const check = confirm(
          "Are you sure you are ready to submit? This will send a notification to your appraiser that you have completed this form."
        );
        if (!check) return;
        yearData.status = "mentor";
        getKey(yearData, "statusTimes", {})[yearData.status] = Date.now();

        backend.send("sendEmail", {
          type: "mentorAlert1",
          data: {
            mentorList: mentorList,
            cvOwnerId: cvOwner.id
          }
        });
      } else if (yearData.status == "mentor") {
        const check = confirm(
          "Are you sure you are ready to finalize? This will send a notification to your colleague that you have completed this review."
        );
        if (!check) return;
        yearData.status = "finalized";
        getKey(yearData, "statusTimes", {})[yearData.status] = Date.now();

        backend.send("sendEmail", {
          type: "menteeAlert1",
          data: {
            mentorId: user.id,
            cvOwnerId: cvOwner.id
          }
        });
      }
      checkSave();
      updateHTML();
    });
  $(".prevStatus")
    .unbind("click")
    .click(function () {
      if (yearData.status == "mentor") yearData.status = "mentee";
      else if (yearData.status == "finalized") yearData.status = "mentor";
      // getKey(yearData, 'statusTimes', {})[yearData.status] = Date.now();
      checkSave();
      updateHTML();
    });
}

function updateSignatures(
  contextConstants,
  contextState,
  conference,
  yearData,
  backend,
  mentorList,
  checkSave
) {
  const { user, users } = contextConstants;
  $("#lahSignSection").toggle(["finalized"].indexOf(yearData.status) >= 0);
  const data = getKey(yearData, "data", {});
  const chairSign = getKey(data, "chairSign", {});
  const menteeSign = getKey(data, "menteeSign", {});
  $("#lahSignChairLine").text(
    "Signature of Appraiser: " +
      (chairSign.id == null
        ? "(Not Yet Signed)"
        : (users[chairSign.id] || { name: "" }).name +
          " (" +
          formatDate(new Date(chairSign.time)) +
          ")")
  );
  if (chairSign.id != null) $("#lahSignChairButton").text("Update");

  $("#lahSignChairButton").toggle(
    !conference_helper.isReadOnly(
      contextConstants,
      contextState,
      conference,
      yearData,
      viewYear,
      "mentor",
      { noOverride: true }
    ) && menteeSign.id != null
  );
  $("#lahSignChairButton")
    .unbind("click")
    .click(function () {
      chairSign.id = user.id;
      chairSign.time = Date.now();
      chairSign.signature = user.name;
      $("#lahSignChairLine").text(
        "Signature of Appraiser: " +
          (chairSign.id == null
            ? "(Not Yet Signed)"
            : (users[chairSign.id] || { name: "" }).name +
              " (" +
              formatDate(new Date(chairSign.time)) +
              ")")
      );
      $("#lahSignChairButton").text("Signed");
      if (chairSign.id != null) {
        backend.send("sendEmail", {
          type: "menteeAlertAPCounterSigned",
          data: {
            cvOwnerId: menteeSign.id
          }
        });
      }

      checkSave(true);
    });

  $("#lahSignMenteeLine").html(
    "<b>Signature of Colleague: </b><i>" +
      (menteeSign.id == null
        ? "(Not Yet Signed)"
        : (users[menteeSign.id] || { name: "" }).name +
          " (" +
          formatDate(new Date(menteeSign.time)) +
          ")") +
      "</i>"
  );
  if (menteeSign.id != null) $("#lahSignMenteeButton").text("Update");
  $("#lahSignMenteeButton").toggle(
    !conference_helper.isReadOnly(
      contextConstants,
      contextState,
      conference,
      yearData,
      viewYear,
      "mentee",
      { noOverride: true }
    )
  );

  $("#lahSignMenteeButton")
    .unbind("click")
    .click(function () {
      menteeSign.id = user.id;
      menteeSign.time = Date.now();
      menteeSign.signature = user.name;
      $("#lahSignMenteeLine").html(
        "<b>Signature of Colleague: </b><i>" +
          (menteeSign.id == null
            ? "(Not Yet Signed)"
            : (users[menteeSign.id] || { name: "" }).name +
              " (" +
              formatDate(new Date(menteeSign.time)) +
              ")") +
          "</i>"
      );
      $("#lahSignMenteeButton").text("Signed");
      if (menteeSign.id != null) {
        backend.send("sendEmail", {
          type: "mentorAlertAPSigned",
          data: {
            mentorList: mentorList,
            cvOwnerId: menteeSign.id
          }
        });
      }

      checkSave();
    });
}

function laheyCCSetup(
  contextMethods,
  contextConstants,
  contextState,
  conference,
  backend,
  dataPrefix,
  templateName,
  downloadName,
  departmentId,
  generationCallback
) {
  const { updateLayout, checkSave } = contextMethods;
  const { user, users, cvOwner, mentors, formats } = contextConstants;
  const {
    section,
    sectionState,
    format,
    newDef,
    viewAsState,
    customExportSettings
  } = contextState;

  const editAccessOverride =
    (user.settings.accessFilters || []).length > 0 &&
    user.settings.conferenceTabAccess == "editAccess";

  let menteeAccess = user.id == cvOwner.id || editAccessOverride;
  updateOverviewState(sectionState, section);
  let mentorAccess =
    (conference.mentors[user.id] || {})[cvOwner.id] != null ||
    editAccessOverride;

  function updateHTML(opts) {
    // Find which years are available. Should be just years which have data or which are open, or if you have mentorAccess
    updateYearInfo(
      newDef,
      conference,
      user,
      cvOwner,
      sectionState,
      section,
      editAccessOverride
    );
    const yearData = getKey(newDef.career, viewYear, {
      timeBreakdown: {},
      goals: {},
      status: "mentee",
      questions: {},
      mentorComments: "",
      details: {}
    });
    const lastYearData = getKey(newDef.career, viewYear - 1, {
      timeBreakdown: {},
      goals: {},
      status: "mentee",
      questions: {},
      mentorComments: "",
      details: {}
    });
    const departmentName = newDef.bio?.dept || "";
    const cvOwnerName = cvOwner.name || "";
    const appraisalPeriod = `${dayjs(startDate).format("M/D/YY")} - ${dayjs(endDate).format("M/D/YY")}`;
    $("#fieldList").empty();
    // Create the download/print buttons
    createExportButtons(cvOwner, contextState.customExportSettings, updateHTML);
    const mentorList = _.keys(conference.mentors).filter(function (id) {
      return conference.mentors[id][cvOwner.id] != null;
    });
    const appraiserName = mentors[mentorList[0]]?.name || "";
    // List all mentors of user
    listMentors(mentors, mentorList);

    const printableContents = $('<span id="printableContents"></span>');
    $("#fieldList").append(printableContents);

    printableContents.append(
      $(conference_helper.generateHTML(newDef, structure.structure))
    );
    createSignSection(printableContents);
    createStatusButtons();
    conference_helper.createOutput(
      templateName,
      contextState,
      contextConstants,
      backend,
      conference,
      downloadName,
      structure.structure,
      {
        departmentName,
        departmentId,
        appraiserName,
        appraisalPeriod,
        yearData,
        lastYearData,
        startDate,
        newDef
      },
      generationCallback
    );
    getKey(yearData, "status", "mentee");
    $(".statusTitle").text(
      "Status: " +
        (!isOpen
          ? "Closed"
          : {
              mentee: "Colleague Editing",
              mentor: "Appraiser Review",
              finalized: "Finalized"
            }[yearData.status])
    );

    $(".nextStatus, .prevStatus").hide();

    if (!isOpen) {
      // Nothing
    } else if (yearData.status === "mentee") {
      if (menteeAccess) {
        $(".nextStatus").show().text("Submit for Review");
      }
    } else if (yearData.status === "mentor") {
      if (menteeAccess && !!confYearData.open) {
        $(".prevStatus").show().text("Resume Editing");
      }
      if (mentorAccess) {
        $(".nextStatus").show().text("Finalize Review");
      }
    } else if (yearData.status === "finalized") {
      if (mentorAccess) {
        $(".prevStatus").show().text("Resume Review");
      }
    }
    conference_helper.updateCareerYear(
      isOpen,
      yearData,
      overviewState,
      availableYears,
      startDate,
      endDate,
      updateHTML
    );
    updateStatusButtons(
      yearData,
      mentorList,
      cvOwner,
      user,
      newDef,
      backend,
      updateHTML,
      checkSave
    );
    conference_helper.setFieldStatus(
      contextConstants,
      contextState,
      conference,
      dataPrefix,
      structure.structure,
      yearData,
      viewYear,
      lastYearData,
      opts
    );

    const readonlyValues = [
      { key: "colleague_name", value: cvOwnerName },
      { key: "appraiser_name", value: appraiserName },
      { key: "appraisal_period", value: appraisalPeriod },
      { key: "department_name", value: departmentName },
      { key: "department_id", value: departmentId }
    ];
    const last = getLastPosition(newDef, { format });
    if (last?.title) {
      readonlyValues.push({
        key: "academic_rank",
        value: last.title
      });
    }
    if (last?.years) {
      readonlyValues.push({
        key: "time_in_position",
        value: last.years
      });
    }
    const storedDateCompleted =
      newDef.career[viewYear].data[dataPrefix + "date_appraisal_completed"];
    if (storedDateCompleted && storedDateCompleted.length) {
      readonlyValues.push({
        key: "date_appraisal_completed",
        value: storedDateCompleted
      });
    }
    const jumpMappings = {
      colleague_name: "bio",
      academic_rank: "facultyAppointments",
      time_in_position: "facultyAppointments",
      department_name: "bio"
    };
    const erroredReadonlyFields = $(".has-error > [class*=conf]").filter(
      "[readonly]"
    );
    erroredReadonlyFields
      .parent()
      .append(
        `<span class="glyphicon glyphicon-share-alt" style="margin-left: 15px; color: #a94442;"></span>`
      );
    erroredReadonlyFields
      .parent()
      .find(".glyphicon-share-alt")
      .on("click", (e) => {
        const jumpTo =
          jumpMappings[e.target?.previousSibling?.getAttribute("data-key")];
        if (jumpTo != undefined) {
          contextState.section = jumpTo;
          contextMethods.refreshSection();
        }
      });
    populateReadonlyValues(readonlyValues, newDef, dataPrefix);
    checkSave();

    $("#lahSignSection").toggle(["finalized"].indexOf(yearData.status) >= 0);

    /* Unused block i think, keeping it here incase
    var outputSource = formats[format];
    if (formats[format].outputs != null) {
      var outputNames = _.keys(formats[format].outputs);
      if (outputNames.indexOf($("#cvOutputFormat").val()) >= 0)
        outputSource = formats[format].outputs[$("#cvOutputFormat").val()];
      else outputSource = formats[format].outputs[outputNames[0]];
    }
    */

    conference_helper.updateCareerInputs(
      isOpen,
      newDef,
      user,
      backend,
      structure.structure,
      viewYear,
      dataPrefix,
      menteeAccess,
      mentorAccess,
      checkSave
    );
    updateSignatures(
      contextConstants,
      contextState,
      conference,
      yearData,
      backend,
      mentorList,
      (mentorSignCall = false) => {
        if (mentorSignCall)
          setAppraisalCompletedDate(mentorAccess, newDef, dataPrefix, yearData);
        checkSave();
      }
    );
    updateLayout();
  }

  updateHTML();
}

function laheyCCManage(
  settingsEl,
  conference,
  updateConfDate,
  saveConference,
  hierarchyNames,
  backend,
  user
) {
  const ccManage = new CCManage(conference, saveConference, updateConfDate, {
    includeSections: {
      cutoff: true,
      mentees: true,
      goals: false,
      questions: false,
      mentors: true,
      years: true,
      reminders: true
    },
    sortBy: "mentor",
    verbage: {
      mentor: "Appraiser",
      mentee: "Mentee"
    }
  });
  ccManage.createSettingsElements(settingsEl, backend);
  updateConfDate(conference);
  ccManage.createMentorsMenteesSection();
  ccManage.createAwesomplete();
  ccManage.hookMessageButton(user);
  ccManage.populateMenteesSection(hierarchyNames);
}

export { ConferenceStructure, laheyCCManage, laheyCCSetup, overrideStructure };
